<template>
  <screening-workflow-panel
    :review="review"
    :current-researcher="currentResearcher"
    :pending-connection.sync="referencesFullTextReviewedConnection"
    :pending-connection-query="
      $apollo.queries.referencesFullTextReviewedConnection
    "
    :search-input.sync="searchInput"
    :get-vote-by-current-user-func="getOutcomeByCurrentUser"
    :revoke-vote-func="onRevokeOutcome"
    :should-hide-reference-func="shouldHideReference"
    :should-hide-reference-based-on-vote-func="
      shouldHideReferenceBasedOnOutcome
    "
    :filtering-input="filteringInput"
    disable-select-next-on-vote
    full-text-review
    @saving-vote="onSavingOutcome"
    @clear-filtering-input="$emit('clear-filtering-input')"
  >
    <template #empty-state>
      <screen-tab-complete-empty-state />
    </template>
  </screening-workflow-panel>
</template>

<script>
import ScreeningWorkflowPanel from '@/components/ScreeningPageV2/ScreenTab/ScreeningWorkflowPanel';
import ScreenTabCompleteEmptyState from '@/components/ScreeningPageV2/ScreenTab/ScreenTabCompleteEmptyState';
import { GetReferencesFullTextReviewed } from '@/graphql';
import {
  Review,
  Researcher,
  ReferenceOffsetConnection,
  ReferenceFilteringInputDto,
} from '@/models';
import {
  InclusionAssessment,
  ScreeningOutcomeResolution,
  AssessmentStatusFilter,
} from '@/enums';
import { v4 as createUUID } from 'uuid';
import { mapActions } from 'vuex';
import { isEqual } from 'lodash';

const PAGE_SIZE = 50;
const ALLOWED_OUTCOMES_TO_FILTERS = {
  [InclusionAssessment.INCLUDE]:
    AssessmentStatusFilter.INCLUDE_OUTCOME,
  [InclusionAssessment.EXCLUDE]:
    AssessmentStatusFilter.EXCLUDE_OUTCOME,
};

export default {
  name: 'ReviewTabComplete',
  components: {
    ScreeningWorkflowPanel,
    ScreenTabCompleteEmptyState,
  },
  props: {
    review: {
      type: Review,
      required: true,
    },
    currentResearcher: {
      type: Researcher,
      required: true,
    },
    filteringInput: {
      type: ReferenceFilteringInputDto,
      required: true,
    },
    filterOutcomesTo: {
      type: Array,
      default: () => [InclusionAssessment.INCLUDE],
    },
    active: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['clear-filtering-input'],
  data: () => ({
    searchInput: null,
    referencesFullTextReviewedConnection: null,
    hiddenReferenceMap: {},
  }),
  apollo: {
    referencesFullTextReviewedConnection: {
      query: GetReferencesFullTextReviewed,
      variables() {
        return {
          reviewId: this.review.id,
          limit: PAGE_SIZE,
          searchQuery: this.searchInput || null,
          outcomes: this.filterOutcomesTo.map(
            (outcome) => ALLOWED_OUTCOMES_TO_FILTERS[outcome]
          ),
          hasTag: this.filteringInput.hasTag,
          hasKeyword: this.filteringInput.hasKeyword,
          hasAuthor: this.filteringInput.hasAuthor,
          hasPlacePublished: this.filteringInput.hasPlacePublished,
        };
      },
      update: (data) =>
        new ReferenceOffsetConnection(
          data.referencesFullTextReviewed
        ),
      skip() {
        return !this.review?.id;
      },
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    },
  },
  watch: {
    active(active) {
      if (active && this.referencesFullTextReviewedConnection) {
        this.$apollo.queries.referencesFullTextReviewedConnection.refetch();
      }
    },
    filteringInput(newInput, oldInput) {
      // Clear the cache when the filtering input changes to show the loading state.
      const changed = !isEqual(newInput, oldInput);
      if (this.active && changed) {
        this.referencesFullTextReviewedConnection = null;
      }
    },
  },
  methods: {
    ...mapActions({
      undoManualOverride: 'fullTextReviewOutcome/undoManualOverride',
      removeConflictResolution:
        'fullTextReviewOutcome/removeConflictResolution',
    }),
    shouldHideReference(reference) {
      return (
        this.hiddenReferenceMap[reference.id] ||
        !this.filterOutcomesTo.includes(
          reference.fullTextReviewOutcome?.outcome
        )
      );
    },
    shouldHideReferenceBasedOnOutcome(fullTextReviewOutcome) {
      return !this.filterOutcomesTo.includes(
        fullTextReviewOutcome.outcome
      );
    },
    getOutcomeByCurrentUser(reference) {
      const fullTextReviewOutcome = reference?.fullTextReviewOutcome;
      if (!fullTextReviewOutcome || !fullTextReviewOutcome.id)
        return null;

      const allowedStates = [
        ScreeningOutcomeResolution.UPDATED_OUTCOME,
        ScreeningOutcomeResolution.CONFLICT_RESOLUTION,
      ];
      const resolvedBy =
        fullTextReviewOutcome.resolvedBy?.researcher.id;
      const researcherId = this.currentResearcher.id;

      return (
        resolvedBy === researcherId &&
        allowedStates.includes(fullTextReviewOutcome.resolution) &&
        fullTextReviewOutcome
      );
    },
    onSavingOutcome(fullTextReviewOutcome) {
      // Instantly hide the reference if the outcome will result in it being hidden.
      this.$set(
        this.hiddenReferenceMap,
        fullTextReviewOutcome.reference.id,
        !this.filterOutcomesTo.includes(fullTextReviewOutcome.outcome)
      );
    },
    onRevokeOutcome({ id, cacheUpdate, reference }) {
      const fullTextReviewOutcome = this.getOutcomeByCurrentUser(
        reference
      );
      if (!fullTextReviewOutcome)
        throw new Error(
          'No full text review outcome by the current user to revoke'
        );

      const lastArchivedOutcome = reference.getLastArchivedFullTextReviewOutcome();
      const previousOutcomeNotInThisPage =
        lastArchivedOutcome &&
        !this.filterOutcomesTo.includes(lastArchivedOutcome.outcome);

      switch (fullTextReviewOutcome.resolution) {
        case ScreeningOutcomeResolution.UPDATED_OUTCOME:
          // Hide the reference is the previous outcome (that is about to become current)
          // would not be in this page.
          this.$set(
            this.hiddenReferenceMap,
            reference.id,
            previousOutcomeNotInThisPage
          );
          return this.undoManualOverride({
            id,
            cacheUpdate: (cache, data) => {
              cacheUpdate(cache, data);

              // Set the previous outcome to the reference.fullTextReviewOutcome
              // cache as an optimistic response. Which should result in the
              // dropdown showing the previous outcome but show a loading
              // indicator until the actual response comes back.
              if (!data.optimisticId) return;
              this.setOptimisticOutcomeOnReference(
                cache,
                reference,
                lastArchivedOutcome
              );
            },
            optimisticResponse: {
              ...fullTextReviewOutcome,
              reference,
            },
          });
        case ScreeningOutcomeResolution.CONFLICT_RESOLUTION:
          // The reference will have no full text review outcome after this, so we must
          // hide it now.
          this.$set(this.hiddenReferenceMap, reference.id, true);
          return this.removeConflictResolution({ id, cacheUpdate });
        default:
          throw new Error(
            `Unexpected full text review outcome resolution to revoke: ${fullTextReviewOutcome.resolution}`
          );
      }
    },
    setOptimisticOutcomeOnReference(cache, reference, outcome) {
      cache.modify({
        id: cache.identify(reference),
        fields: {
          fullTextReviewOutcome(_, { toReference }) {
            return toReference(
              {
                id: outcome.id,
                optimisticId: createUUID(),
                __typename: 'FullTextReviewOutcome',
              },
              true // merge the above data with the existing data in the cache
            );
          },
        },
      });
    },
  },
};
</script>
